import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { makeStyles } from '@mui/styles';
import {
	colors,
	IconButton,
	Tooltip,
} from '@mui/material';

import CreateIcon from '@mui/icons-material/Create';
import EmailIcon from '@mui/icons-material/Email';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import DeleteIcon from '@mui/icons-material/Delete';
import Label from '../../../../../../components/Label/Label.js';
import DataTable from '../../../../../../components/DataTable/DataTable.js';
import { ToggleAddEditUserDialog, ResendConfirmationEmail, DeactivateUser, ToggleDeleteUserDialog } from '../../../../actions/UsersActions.js';

const useStyles = makeStyles((theme) => ({
	root: {},
	content: {
		padding: 0,
	},
	inner: {
		minWidth: 700,
	},
	nameCell: {
		display: 'flex',
		alignItems: 'center',
	},
	avatar: {
		height: 42,
		width: 42,
		marginRight: theme.spacing(1),
	},
	actions: {
		padding: theme.spacing(1),
		justifyContent: 'flex-end',
	},
	primaryButton: {
		color: theme.palette.primary.main
	},
	errorButton: {
		color: theme.palette.error.main
	}
}));

const UsersDataTable = (props) => {
	const { className, data, ...rest } = props;
	const dispatch = useDispatch();
	const classes = useStyles();

	const _editUser = (userId) => {
		dispatch(ToggleAddEditUserDialog(true, userId));
	};

	const _deleteUser = (userId) => {
		dispatch(ToggleDeleteUserDialog(true, userId));
	};

	const _resendConfirmationEmail = (userId) => {
		dispatch(ResendConfirmationEmail(userId));
	};

	const _deactivateUser = (userId) => {
		dispatch(DeactivateUser(userId, rest.user.role));
	};

	const _renderActions = (tableMeta) => {
		const { rowData } = tableMeta;
		return (
			<>
				{rowData[5] ? (
					<Tooltip title="Deactivate">
						<IconButton edge="end" size="small" className={classes.primaryButton} onClick={() => _deactivateUser(rowData[0])}>
							<ExitToAppIcon />
						</IconButton>
					</Tooltip>
				) : (
					<Tooltip title="Resend confirmation email">
						<IconButton edge="end" size="small" className={classes.primaryButton} onClick={() => _resendConfirmationEmail(rowData[0])}>
							<EmailIcon />
						</IconButton>
					</Tooltip>
				)}
				<Tooltip title="Edit">
					<IconButton edge="end" size="small" className={classes.primaryButton} onClick={() => _editUser(rowData[0])}>
						<CreateIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Delete">
					<IconButton edge="end" size="small" className={classes.errorButton} onClick={() => _deleteUser(rowData[0])}>
						<DeleteIcon />
					</IconButton>
				</Tooltip>
			</>

		);
	};

	const _getTableColumns = () => (
		[
			{
				name: 'id',
				label: 'Id',
				options: {
					display: false,
					filter: false,
					viewColumns: false,
					filterType: 'textField',
				}
			},
			{
				name: 'name',
				label: 'Name',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'email',
				label: 'Email',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'role',
				label: 'Role',
				options: {
					display: true,
					filterType: 'textField',
				}
			},
			{
				name: 'operators',
				label: 'Operators',
				options: {
					display: true,
					filterType: 'textField',
					customBodyRender: (value) => {
						if (value) {
							const values = value.split(',');
							const sortedValues = _.sortBy(values, val => val);
							const operators = sortedValues.map((operator, index) => (
								<>
									{operator}
									<br />
								</>
							));
							return operators;
						}
						return null;
					}
				}
			},
			{
				name: 'status',
				label: 'Status',
				options: {
					display: true,
					filterType: 'textField',
					customBodyRender: (value) => {
						if (value !== null) {
							return (
								<Label
									color={
										value
											? colors.green[600]
											: colors.orange[600]
									}
								>
									{value
										? 'Email confirmed'
										: 'Email not confirmed'}
								</Label>
							);
						}
						return null;
					}
				}
			},
			{
				name: 'online',
				label: 'Online',
				options: {
					display: true,
					filterType: 'textField',
					customBodyRender: (value) => {
						if (value !== null) {
							return (
								<Label
									color={
										value
											? colors.green[600]
											: colors.red[600]
									}
								>
									{value
										? 'Online'
										: 'Offline'}
								</Label>
							);
						}
						return null;
					}
				}
			},
			{
				name: 'action',
				label: 'Actions',
				options: {
					filter: false,
					sort: false,
					viewColumns: false,
					empty: true,
					customBodyRender: (value, tableMeta, updateValue) => _renderActions(tableMeta)
				}
			},
		]
	);

	const _getTableOptions = () => ({
		filterType: 'dropdown',
		selectableRows: 'none',
		expandableRows: false, // Try Adding This
	});

	return (
		<div {...rest} className={clsx(classes.root, className)}>
			<DataTable
				id="usersDataTble"
				title="All Users"
				data={data}
				columns={_getTableColumns()}
				rowsPerPage={10}
				tableBodyMaxHeight="30em"
				tableBodyCellMaxWidth="250"
				options={_getTableOptions()}
			/>
		</div>
	);
};

UsersDataTable.propTypes = {
	className: PropTypes.string,
	data: PropTypes.array.isRequired,
};

export default UsersDataTable;
